import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Clock, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import B2BLayout from "../business/B2BLayout";
import { api } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_TONE = {
    submitted: "bg-blue-100 text-blue-700",
    approved: "bg-emerald-100 text-emerald-700",
    processing: "bg-amber-100 text-amber-800",
    dispatched: "bg-violet-100 text-violet-700",
    delivered: "bg-emerald-200 text-emerald-800",
    invoiced: "bg-brand-100 text-brand-800",
    paid: "bg-emerald-300 text-emerald-900",
    closed: "bg-zinc-200 text-zinc-700",
    cancelled: "bg-rose-100 text-rose-700",
};

const BusinessOrders = () => {
    const { id } = useParams();
    const [list, setList] = useState([]);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let m = true;
        const load = async () => {
            setLoading(true);
            try {
                if (id) {
                    const { data } = await api.get(`/b2b/orders/${id}`);
                    if (m) setDetail(data);
                } else {
                    const { data } = await api.get("/b2b/orders", { params: { page_size: 50 } });
                    if (m) setList(data.items || []);
                }
            } finally {
                if (m) setLoading(false);
            }
        };
        load();
        return () => { m = false; };
    }, [id]);

    const cancel = async (orderId) => {
        if (!window.confirm("Cancel this order? Cannot be undone.")) return;
        try {
            await api.post(`/b2b/orders/${orderId}/cancel`);
            toast.success("Order cancelled");
            const { data } = await api.get(`/b2b/orders/${orderId}`);
            setDetail(data);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not cancel");
        }
    };

    return (
        <B2BLayout
            title={id ? "Order details" : "Orders"}
            subtitle="B2B orders"
            action={id && <Link to="/business/orders" className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"><ArrowLeft className="h-3 w-3" /> All orders</Link>}
        >
            {() => {
                if (loading) return <div className="h-64 skeleton rounded-3xl" />;

                // -------- Detail view --------
                if (id && detail) {
                    return (
                        <div className="space-y-5" data-testid={`b2b-order-detail-${id}`}>
                            <div className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div>
                                        <p className="font-mono text-sm text-slate-400">{detail.order_no}</p>
                                        <h2 className="mt-0.5 text-lg font-semibold text-zinc-900">{inr(detail.amounts?.total)}</h2>
                                        <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[detail.status]}`}>{detail.status.replace("_", " ")}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {detail.invoice_id && <Link to={`/business/invoices`} className="rounded-full bg-brand-700 px-3 py-1.5 text-xs text-white hover:bg-brand-800" data-testid="b2b-order-invoice-link"><FileText className="-mt-0.5 mr-1 inline h-3 w-3" /> View invoice</Link>}
                                        {["draft", "submitted"].includes(detail.status) && (
                                            <button onClick={() => cancel(detail.id)} className="rounded-full border border-rose-200 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50" data-testid="b2b-order-cancel">Cancel</button>
                                        )}
                                    </div>
                                </div>
                                <p className="mt-3 text-xs text-slate-500">
                                    {detail.items?.length || 0} items · {(detail.payment_terms || "").replace("_", " ")} · Submitted {new Date(detail.created_at).toLocaleString()}
                                </p>
                            </div>

                            <div className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft">
                                <p className="label-uppercase mb-2">Items</p>
                                <ul className="divide-y divide-zinc-50">
                                    {(detail.items || []).map((it) => (
                                        <li key={it.product_id} className="flex items-center justify-between py-2.5 text-sm">
                                            <div className="min-w-0">
                                                <p className="font-medium text-zinc-900">{it.name}</p>
                                                <p className="text-xs text-slate-500">{it.brand} · {it.pack_size} · {it.qty} × {inr(it.unit_price)}</p>
                                            </div>
                                            <p className="font-semibold">{inr(it.subtotal)}</p>
                                        </li>
                                    ))}
                                </ul>
                                <dl className="mt-4 space-y-1.5 border-t pt-3 text-sm">
                                    <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd>{inr(detail.amounts?.subtotal)}</dd></div>
                                    <div className="flex justify-between"><dt className="text-slate-500">GST</dt><dd>{inr(detail.amounts?.gst_amount)}</dd></div>
                                    <div className="flex justify-between font-bold"><dt>Total</dt><dd>{inr(detail.amounts?.total)}</dd></div>
                                </dl>
                            </div>

                            <div className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft" data-testid="b2b-order-history">
                                <p className="label-uppercase mb-2">Status timeline</p>
                                <ol className="space-y-2">
                                    {(detail.history || []).map((h, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm">
                                            <Clock className="mt-0.5 h-3 w-3 text-slate-400" />
                                            <div>
                                                <span className="font-medium capitalize">{h.event.replace("_", " ")}</span>
                                                <span className="text-xs text-slate-500"> · {new Date(h.at).toLocaleString()}{h.note ? ` · ${h.note}` : ""}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    );
                }

                // -------- List view --------
                if (list.length === 0) {
                    return (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="b2b-orders-empty">
                            <p className="text-sm text-slate-500">No orders yet.</p>
                            <Link to="/business/shop" className="btn-brand mt-4 !text-sm">Browse wholesale catalog</Link>
                        </div>
                    );
                }

                return (
                    <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm" data-testid="b2b-orders-list">
                        <table className="w-full text-sm">
                            <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                                <tr>
                                    <th className="px-4 py-3">Order #</th>
                                    <th className="px-4 py-3">Items</th>
                                    <th className="px-4 py-3">Payment terms</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3 text-right">Total</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-50">
                                {list.map((o) => (
                                    <tr key={o.id} className="hover:bg-zinc-50" data-testid={`b2b-orders-row-${o.id}`}>
                                        <td className="px-4 py-3 font-mono">{o.order_no}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{o.items?.length || 0} · {new Date(o.created_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 text-xs">{(o.payment_terms || "").replace("_", " ")}</td>
                                        <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[o.status]}`}>{o.status.replace("_", " ")}</span></td>
                                        <td className="px-4 py-3 text-right font-semibold">{inr(o.amounts?.total)}</td>
                                        <td className="px-4 py-3"><Link to={`/business/orders/${o.id}`} className="text-xs text-brand-700 hover:underline">Open →</Link></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            }}
        </B2BLayout>
    );
};

export default BusinessOrders;
